<template>
  <div class="automation-widget">
    <TransitionGroup name="widget">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="panel"
        :class="{ 'panel--running': entry.kind === 'running' }"
      >
        <div class="status">
          <Spinner
            v-if="entry.kind === 'running' || entry.status === 'pending_resume'"
          />
          <TriangleWarningIcon
            v-else-if="entry.status === 'failed'"
            class="status-icon status-icon--failed"
          />
          <QuestionIcon
            v-else-if="entry.status === 'pending_user_input'"
            class="status-icon status-icon--waiting"
          />
          <CheckmarkIcon v-else class="status-icon status-icon--completed" />
        </div>
        <div class="content">
          <div class="name">{{ entry.name }}</div>
          <div v-if="entry.kind === 'running'" class="detail">
            Step {{ entry.stepIndex }} of {{ entry.steps
            }}<template v-if="entry.stepName"> · {{ entry.stepName }}</template>
          </div>
          <div
            v-else
            class="detail"
            :class="{ 'detail--failed': entry.status === 'failed' }"
          >
            {{ statusLabel(entry.status) }}
          </div>
        </div>
        <div v-if="entry.kind === 'finished'" class="actions">
          <BaseButton
            kind="auxiliary"
            text="View result"
            :state="
              entry.automationId && entry.organizationId
                ? 'default'
                : 'disabled'
            "
            @click="viewRun(entry)"
          />
          <BaseButton
            kind="auxiliary"
            class="dismiss"
            @click="dismiss(entry.id)"
          >
            <template #icon><CloseIcon /></template>
          </BaseButton>
        </div>
        <div v-if="entry.kind === 'running'" class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: progressPercent(entry) }"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";

import type {
  AiAutomationRunning,
  AutomationRunId,
  OrgId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import CheckmarkIcon from "@/components/icons/CheckmarkIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import QuestionIcon from "@/components/icons/QuestionIcon.vue";
import TriangleWarningIcon from "@/components/icons/TriangleWarningIcon.vue";
import * as inject from "@/components/inject";
import Spinner from "@/components/Spinner.vue";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

type RunStatus =
  | "completed"
  | "failed"
  | "pending_user_input"
  | "pending_resume"
  | null;

type FinishedRun = {
  id: AutomationRunId;
  name: string;
  /** `null` while the automation id / final status is still being fetched. */
  automationId: string | null;
  organizationId: OrgId | null;
  /** `null` while the final status is still being fetched. */
  status: RunStatus;
};

type RunningEntry = {
  kind: "running";
  id: AutomationRunId;
  name: string;
  steps: number;
  stepIndex: number;
  stepName: string;
};

type FinishedEntry = { kind: "finished" } & FinishedRun;

type WidgetEntry = RunningEntry | FinishedEntry;

export default defineComponent({
  name: "AutomationWidget",
  components: {
    BaseButton,
    CheckmarkIcon,
    CloseIcon,
    QuestionIcon,
    Spinner,
    TriangleWarningIcon,
  },
  setup() {
    const automations = inject.automations();
    const controller = inject.controller();
    const profile = inject.profile();
    const router = inject.router();

    const runningAutomations = computed(
      () => automations.value?.running.value ?? []
    );

    // Runs that were observed as running and have since disappeared from the
    // running list. They stay visible (with a link to the run detail) until
    // explicitly dismissed.
    const finishedRuns = ref<FinishedRun[]>([]);

    const resolveRunStatus = async (run: AiAutomationRunning) => {
      let status: RunStatus = null;
      let automationId: string | null = null;
      const organizationId = getOrganizationIdFromWorkspaceId(
        profile.value,
        run.workspace_id
      );
      try {
        if (organizationId) {
          const result = (
            await controller.ai.automation.run.get(organizationId, run.id)
          ).release();
          status = result?.status.status ?? null;
          automationId = result?.automation_id ?? null;
        }
      } catch {
        // Status stays unknown; the entry still links to the run detail.
      }
      finishedRuns.value = finishedRuns.value.map((finished) =>
        finished.id === run.id
          ? {
              ...finished,
              automationId,
              organizationId: organizationId ?? finished.organizationId,
              status,
            }
          : finished
      );
    };

    watch(runningAutomations, (next, prev) => {
      const stillRunning = new Set(next.map((run) => run.id));
      for (const run of prev) {
        const alreadyTracked = finishedRuns.value.some(
          (finished) => finished.id === run.id
        );
        if (!stillRunning.has(run.id) && !alreadyTracked) {
          const organizationId = getOrganizationIdFromWorkspaceId(
            profile.value,
            run.workspace_id
          );
          finishedRuns.value = [
            ...finishedRuns.value,
            {
              id: run.id,
              name: run.name,
              automationId: null,
              organizationId: organizationId ?? null,
              status: null,
            },
          ];
          void resolveRunStatus(run);
        }
      }
    });

    const entries = computed<WidgetEntry[]>(() => [
      ...runningAutomations.value.map(
        (run): RunningEntry => ({
          kind: "running",
          id: run.id,
          name: run.name,
          steps: run.steps,
          stepIndex: run.step.index,
          stepName: run.step.name,
        })
      ),
      ...finishedRuns.value.map(
        (finished): FinishedEntry => ({ kind: "finished", ...finished })
      ),
    ]);

    const progressPercent = (entry: RunningEntry) => {
      const ratio = entry.steps > 0 ? entry.stepIndex / entry.steps : 0;
      return `${Math.min(100, Math.max(0, ratio * 100))}%`;
    };

    const dismiss = (id: AutomationRunId) => {
      finishedRuns.value = finishedRuns.value.filter(
        (finished) => finished.id !== id
      );
    };

    const statusLabel = (status: RunStatus) => {
      switch (status) {
        case "failed":
          return "Failed";
        case "pending_user_input":
          return "Waiting for input";
        case "pending_resume":
          return "Resuming…";
        default:
          return "Completed";
      }
    };

    const viewRun = async (entry: FinishedEntry) => {
      if (!entry.automationId || !entry.organizationId) return;
      await navigateTo(RouteNames.Automation, router, {
        params: { objectId: entry.automationId },
        query: {
          organizationId: entry.organizationId,
          runId: entry.id,
        },
      });
      dismiss(entry.id);
    };

    return { entries, progressPercent, dismiss, statusLabel, viewRun };
  },
});
</script>

<style scoped lang="scss">
.automation-widget {
  position: fixed;
  bottom: 32px;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: $z-index-modal;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
}

.panel {
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 280px;
  max-width: 440px;
  padding: 8px 10px;
  border-radius: $border-radius-l;
  box-shadow: $card;
  @include themed() {
    background: t($bg);
    border: 1px solid t($grey-10);
  }
}

.status {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.status-icon {
  width: 14px;
  height: 14px;
}

.status-icon--completed :deep(.stroke) {
  @include themed() {
    stroke: t($tertiary-100);
  }
}

.status-icon--failed :deep(.fill) {
  @include themed() {
    fill: t($secondary-80);
  }
}

.status-icon--waiting :deep(.fill) {
  @include themed() {
    fill: t($tertiary-100);
  }
}

.content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.name {
  @extend %font-caption-medium;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include themed() {
    color: t($primary);
  }
}

.detail {
  @extend %font-small-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include themed() {
    color: t($grey-50);
  }
}

.detail--failed {
  @include themed() {
    color: t($secondary-80);
  }
}

.actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 2px;
}

.dismiss :deep(svg) {
  width: 10px;
  height: 10px;
}

.progress-track {
  position: absolute;
  bottom: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  height: 1px;
  @include themed() {
    background: t($grey-10);
  }
}

.progress-fill {
  height: 100%;
  @include transition(width 200ms ease-out);
  @include themed() {
    background: t($tertiary-100);
  }
}

.widget-enter-active,
.widget-move {
  @include transition(
    opacity 200ms cubic-bezier(0.23, 1, 0.32, 1),
    transform 200ms cubic-bezier(0.23, 1, 0.32, 1)
  );
}

.widget-leave-active {
  @include transition(
    opacity 150ms cubic-bezier(0.21, 0, 0.38, 0.78),
    transform 150ms cubic-bezier(0.21, 0, 0.38, 0.78)
  );
}

.widget-enter-from,
.widget-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}
</style>
